/**
 * Migration Note: seedDiscriminated.types.ts (formerly SeedDiscriminated.ts)
 *
 * Previous location: src/sections/seeds/types/SeedDiscriminated.ts
 * New location: src/sections/seeds/types/seedDiscriminated.types.ts
 * Migrated: 2025-10-22 (Phase 2)
 * Reason: Standardize type file naming with .types.ts suffix and camelCase
 *
 * Phase 1 Update (2025-10-24):
 * - Replaced SeedPreseed with PreseedSeed adapter type
 * - PreseedSeed now uses adapter pattern for preseed integration
 * - Enables unified handling of all seed types in SeedsTable
 *
 * Breaking changes: Import path updated
 */

import { SeedDomain } from './seedDomain.types';
import { SeedIP } from './seedIp.types';
import { PreseedSeed } from './seedPreseed.types';
import { SeedWebApplication } from './seedWebApplication.types';

/**
 * Discriminated union of all seed types
 *
 * Discriminated by the `class` field which determines the seed's type.
 * Use type guards for type-safe narrowing in conditional logic.
 *
 * Now includes PreseedSeed as a first-class seed type alongside domain/ip/webapp.
 * This enables preseed to work with standard seed components (SeedsTable, SeedsFilters, etc.)
 * while maintaining preseed-specific functionality through the adapter pattern.
 *
 * @example
 * ```typescript
 * const renderSeedDetails = (seed: Seed) => {
 *   if (isSeedDomain(seed)) {
 *     // TypeScript knows: seed is SeedDomain
 *     return <span>{seed.email}</span>;
 *   }
 *   if (isSeedIP(seed)) {
 *     // TypeScript knows: seed is SeedIP
 *     return <span>{seed.country}</span>;
 *   }
 *   if (isSeedPreseed(seed)) {
 *     // TypeScript knows: seed is PreseedSeed
 *     return <span>{seed.preseedType}: {seed.preseedValue}</span>;
 *   }
 *   // ... handle other types
 * };
 * ```
 */
export type Seed = SeedDomain | SeedIP | SeedWebApplication | PreseedSeed;

/**
 * Alias for Seed discriminated union (used in implementation plan)
 *
 * Provides a semantic name for the unified seed entity type.
 * Use this when referring to any seed type in generic contexts.
 */
export type SeedEntity = Seed;

/**
 * Type guard for Domain seeds
 *
 * @param seed - Seed to check
 * @returns true if seed is a SeedDomain
 */
export const isSeedDomain = (seed: Seed): seed is SeedDomain =>
  seed.class === 'domain' || seed.class === 'tld';

/**
 * Type guard for IP/CIDR seeds
 *
 * @param seed - Seed to check
 * @returns true if seed is a SeedIP
 */
export const isSeedIP = (seed: Seed): seed is SeedIP =>
  seed.class === 'ip' ||
  seed.class === 'cidr' ||
  seed.class === 'ipv4' ||
  seed.class === 'ipv6';

/**
 * Type guard for Web Application seeds
 *
 * @param seed - Seed to check
 * @returns true if seed is a SeedWebApplication
 */
export const isSeedWebApplication = (seed: Seed): seed is SeedWebApplication =>
  seed.class === 'webapplication';

/**
 * Type guard for Preseed entries
 *
 * @param seed - Seed to check
 * @returns true if seed is a PreseedSeed
 */
export const isSeedPreseed = (seed: Seed): seed is PreseedSeed =>
  seed.class === 'preseed';

/**
 * Get the high-level seed category from a seed object
 *
 * Maps specific class values to broader categories for filtering and display.
 *
 * @param seed - Seed to categorize
 * @returns The seed's category ('domain', 'ip', 'webapplication', or 'preseed')
 */
export const getSeedCategory = (
  seed: Seed
): 'domain' | 'ip' | 'webapplication' | 'preseed' => {
  if (isSeedDomain(seed)) return 'domain';
  if (isSeedIP(seed)) return 'ip';
  if (isSeedWebApplication(seed)) return 'webapplication';
  if (isSeedPreseed(seed)) return 'preseed';

  // Fallback (should never reach here with proper type system)
  return 'domain';
};

/**
 * Get seed type using discriminator field
 *
 * More efficient than getSeedCategory as it directly checks the class field
 * without multiple type guard calls. Useful in config-driven contexts.
 *
 * @param seed - Seed entity to categorize
 * @returns The seed's type ('domain', 'ip', 'webapplication', or 'preseed')
 * @throws Error if seed class is unknown
 *
 * @example
 * ```typescript
 * const seed: SeedEntity = { class: 'preseed', ... };
 * const type = getSeedType(seed); // 'preseed'
 * const config = getSeedTypeConfig(type); // Get type-specific configuration
 * ```
 */
export const getSeedType = (
  seed: SeedEntity
): 'domain' | 'ip' | 'webapplication' | 'preseed' => {
  if (seed.class === 'preseed') return 'preseed';
  if (seed.class === 'domain' || seed.class === 'tld') return 'domain';
  if (
    seed.class === 'ipv4' ||
    seed.class === 'ipv6' ||
    seed.class === 'cidr' ||
    seed.class === 'ip'
  )
    return 'ip';
  if (seed.class === 'webapplication') return 'webapplication';

  throw new Error(`Unknown seed class: ${seed.class}`);
};

/**
 * Check if a seed class value represents a domain type
 *
 * @param classValue - The seed's class field value
 * @returns true if class represents a domain seed
 */
export const isDomainClass = (classValue: string): boolean =>
  classValue === 'domain' || classValue === 'tld';

/**
 * Check if a seed class value represents an IP type
 *
 * @param classValue - The seed's class field value
 * @returns true if class represents an IP/CIDR seed
 */
export const isIPClass = (classValue: string): boolean =>
  classValue === 'ip' ||
  classValue === 'cidr' ||
  classValue === 'ipv4' ||
  classValue === 'ipv6';

/**
 * Check if a seed class value represents a web application type
 *
 * @param classValue - The seed's class field value
 * @returns true if class represents a web application seed
 */
export const isWebApplicationClass = (classValue: string): boolean =>
  classValue === 'webapplication';

/**
 * Check if a seed class value represents a preseed type
 *
 * @param classValue - The seed's class field value
 * @returns true if class represents a preseed
 */
export const isPreseedClass = (classValue: string): boolean =>
  classValue === 'preseed';
